import { useState, useEffect, useCallback, useRef } from 'react';
import { lookbookItems } from '../../data/mock';
import { ChevronUp, X, Pause, Play } from 'lucide-react';

const STORY_DURATION = 5000; // 5 seconds per story

export const StoriesViewer = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [textVisible, setTextVisible] = useState(false);
  const [likeAnimation, setLikeAnimation] = useState(false);
  const intervalRef = useRef(null);
  const containerRef = useRef(null);

  const items = lookbookItems;

  const goToNext = useCallback(() => {
    if (currentIndex < items.length - 1) {
      setIsTransitioning(true);
      setTextVisible(false);
      setTimeout(() => {
        setCurrentIndex((prev) => prev + 1);
        setProgress(0);
        setIsTransitioning(false);
      }, 300);
    } else {
      setIsOpen(false);
    }
  }, [currentIndex, items.length]);

  const goToPrev = useCallback(() => {
    if (currentIndex > 0) {
      setIsTransitioning(true);
      setTextVisible(false);
      setTimeout(() => {
        setCurrentIndex((prev) => prev - 1);
        setProgress(0);
        setIsTransitioning(false);
      }, 300);
    }
  }, [currentIndex]);

  // Auto-advance timer
  useEffect(() => {
    if (!isOpen || isPaused) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }

    const tickRate = 30;
    const increment = (tickRate / STORY_DURATION) * 100;

    intervalRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          goToNext();
          return 0;
        }
        return prev + increment;
      });
    }, tickRate);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isOpen, isPaused, goToNext]);

  // Show text after image loads
  useEffect(() => {
    if (isOpen && !isTransitioning) {
      const timer = setTimeout(() => setTextVisible(true), 400);
      return () => clearTimeout(timer);
    }
  }, [isOpen, currentIndex, isTransitioning]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') goToNext();
      if (e.key === 'ArrowLeft') goToPrev();
      if (e.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, goToNext, goToPrev]);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleTap = (e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = e.clientX - rect.left;
    const third = rect.width / 3;

    if (x < third) {
      goToPrev();
    } else if (x > third * 2) {
      goToNext();
    } else {
      // Middle tap - pause/play
      setIsPaused((p) => !p);
    }
  };

  const handleDoubleTap = () => {
    setLikeAnimation(true);
    setTimeout(() => setLikeAnimation(false), 1000);
  };

  const openStories = (index = 0) => {
    setCurrentIndex(index);
    setProgress(0);
    setTextVisible(false);
    setIsOpen(true);
  };

  const currentItem = items[currentIndex];

  return (
    <>
      {/* Story Bubbles - Entry Point */}
      <section id="collection" className="bg-[#0A0A0A] py-16 lg:py-20">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-16">
          <div className="flex items-center justify-between mb-8">
            <div>
              <p className="text-[#C4A265] tracking-[0.4em] uppercase text-[11px] mb-2">
                The Collection
              </p>
              <h2 className="font-serif text-3xl md:text-4xl text-[#F5F1EC] leading-[1.05]">
                <span className="font-light">Tap to</span>{' '}
                <span className="italic">Explore</span>
              </h2>
            </div>
            <p className="text-[#5A5550] text-xs tracking-wider hidden md:block">
              {items.length} STORIES
            </p>
          </div>

          {/* Story Circles Row */}
          <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory">
            {items.map((item, index) => (
              <button
                key={item.id}
                onClick={() => openStories(index)}
                className="flex flex-col items-center gap-2.5 shrink-0 group bg-transparent border-none cursor-pointer snap-start"
              >
                <div className="relative w-[72px] h-[72px] md:w-[84px] md:h-[84px] rounded-full p-[2px] bg-gradient-to-br from-[#C4A265] via-[#E8D5A8] to-[#C4A265] group-hover:from-[#E8D5A8] group-hover:to-[#C4A265] transition-all duration-300">
                  <div className="w-full h-full rounded-full overflow-hidden border-[2.5px] border-[#0A0A0A]">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                </div>
                <span className="text-[10px] text-[#8A8580] group-hover:text-[#C4A265] tracking-wider uppercase transition-colors duration-300 max-w-[80px] truncate">
                  {item.title}
                </span>
              </button>
            ))}
          </div>

          {/* Preview Grid - Teaser */}
          <div
            className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-2 cursor-pointer"
            onClick={() => openStories(0)}
          >
            {items.slice(0, 4).map((item, i) => (
              <div
                key={item.id}
                className="relative aspect-[3/4] overflow-hidden group"
              >
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/60 to-transparent" />
                <div className="absolute bottom-3 left-3 right-3">
                  <p className="text-[#C4A265] text-[9px] tracking-[0.25em] uppercase">
                    {item.subtitle}
                  </p>
                  <p className="font-serif text-sm text-[#F5F1EC] italic">
                    {item.title}
                  </p>
                </div>
                {i === 3 && (
                  <div className="absolute inset-0 bg-[#0A0A0A]/50 flex items-center justify-center">
                    <span className="font-serif text-2xl text-[#F5F1EC] italic">
                      +{items.length - 4}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Full-screen Stories Viewer */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[100] bg-[#000]"
          style={{
            animation: 'storiesOpen 0.3s ease-out',
          }}
        >
          {/* Progress Bars */}
          <div className="absolute top-0 left-0 right-0 z-30 px-3 pt-3 flex gap-1">
            {items.map((_, i) => (
              <div
                key={i}
                className="flex-1 h-[2.5px] bg-white/20 overflow-hidden rounded-full"
              >
                <div
                  className="h-full bg-white rounded-full"
                  style={{
                    width:
                      i < currentIndex
                        ? '100%'
                        : i === currentIndex
                        ? `${Math.min(progress, 100)}%`
                        : '0%',
                    transition:
                      i === currentIndex
                        ? 'width 30ms linear'
                        : 'width 0.3s ease',
                  }}
                />
              </div>
            ))}
          </div>

          {/* Header */}
          <div className="absolute top-6 left-0 right-0 z-30 px-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-[#C4A265] flex items-center justify-center">
                <span className="font-serif text-[10px] text-[#0A0A0A] font-bold">
                  ATD
                </span>
              </div>
              <div>
                <p className="text-white text-[13px] font-medium">
                  All The Details
                </p>
                <p className="text-white/50 text-[10px]">
                  {currentIndex + 1} of {items.length}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsPaused((p) => !p)}
                className="text-white/70 hover:text-white bg-transparent border-none cursor-pointer transition-colors duration-200"
              >
                {isPaused ? <Play size={18} /> : <Pause size={18} />}
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="text-white/70 hover:text-white bg-transparent border-none cursor-pointer transition-colors duration-200"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Image */}
          <div
            ref={containerRef}
            className="absolute inset-0 cursor-pointer"
            onClick={handleTap}
            onDoubleClick={handleDoubleTap}
          >
            <img
              src={currentItem.image}
              alt={currentItem.title}
              className="w-full h-full object-cover"
              style={{
                opacity: isTransitioning ? 0 : 1,
                transform: isTransitioning ? 'scale(1.05)' : 'scale(1)',
                transition: 'all 0.3s ease',
                animation: !isTransitioning ? 'storyKenBurns 6s ease-out forwards' : 'none',
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#000]/70 via-transparent to-[#000]/30" />
          </div>

          {/* Double-tap heart animation */}
          {likeAnimation && (
            <div className="absolute inset-0 z-40 flex items-center justify-center pointer-events-none">
              <div className="story-heart-pop text-white text-6xl">♥</div>
            </div>
          )}

          {/* Bottom Text Overlay */}
          <div
            className="absolute bottom-0 left-0 right-0 z-20 p-6 md:p-10"
            style={{
              opacity: textVisible ? 1 : 0,
              transform: textVisible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
            }}
          >
            <p className="text-[#C4A265] text-[10px] tracking-[0.3em] uppercase mb-1.5">
              {currentItem.subtitle}
            </p>
            <h3 className="font-serif text-3xl md:text-4xl text-white italic mb-2">
              {currentItem.title}
            </h3>
            <div className="flex items-center gap-4 mt-3">
              <div className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-[#C4A265]" />
                <span className="text-white/50 text-[10px] tracking-wider uppercase">
                  Swipe or tap to navigate
                </span>
              </div>
            </div>
          </div>

          {/* Navigation Hint Arrows */}
          <div className="absolute left-3 top-1/2 -translate-y-1/2 z-20 opacity-0 hover:opacity-100 transition-opacity duration-300">
            {currentIndex > 0 && (
              <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center backdrop-blur-sm">
                <ChevronUp size={16} className="text-white -rotate-90" />
              </div>
            )}
          </div>
          <div className="absolute right-3 top-1/2 -translate-y-1/2 z-20 opacity-0 hover:opacity-100 transition-opacity duration-300">
            {currentIndex < items.length - 1 && (
              <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center backdrop-blur-sm">
                <ChevronUp size={16} className="text-white rotate-90" />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
